import { createAppRequest } from './request';

export type OpenPlatformTokenResult = {
  accessToken: string;
  exp: number;
  iat: number;
};

const TOKEN_REFRESH_SKEW_MS = 60_000;
const request = createAppRequest();

let cachedToken: OpenPlatformTokenResult | null = null;
let pendingTokenRequest: Promise<OpenPlatformTokenResult> | null = null;

const isReusable = (token: OpenPlatformTokenResult): boolean =>
  token.exp * 1000 > Date.now() + TOKEN_REFRESH_SKEW_MS;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseTokenResult = (value: unknown): OpenPlatformTokenResult => {
  if (!isRecord(value)) {
    throw new Error('开放平台 Token 响应不符合契约');
  }

  if (
    typeof value.accessToken !== 'string' ||
    value.accessToken.length === 0 ||
    typeof value.exp !== 'number' ||
    !Number.isFinite(value.exp) ||
    typeof value.iat !== 'number' ||
    !Number.isFinite(value.iat)
  ) {
    throw new Error('开放平台 Token 响应不符合契约');
  }

  return {
    accessToken: value.accessToken,
    exp: value.exp,
    iat: value.iat,
  };
};

const requestToken = async (): Promise<OpenPlatformTokenResult> => {
  const result = await request<OpenPlatformTokenResult>(
    '/bff/open-platform/token',
    {
      method: 'GET',
    },
  );
  return parseTokenResult(result);
};

export const getOpenPlatformToken = async (
  forceRefresh = false,
): Promise<OpenPlatformTokenResult> => {
  if (!forceRefresh && cachedToken && isReusable(cachedToken)) {
    return cachedToken;
  }

  if (forceRefresh) {
    cachedToken = null;
  }

  if (pendingTokenRequest) {
    return pendingTokenRequest;
  }

  pendingTokenRequest = requestToken()
    .then((token) => {
      cachedToken = token;
      return token;
    })
    .finally(() => {
      pendingTokenRequest = null;
    });

  return pendingTokenRequest;
};

export const clearOpenPlatformToken = (): void => {
  cachedToken = null;
};
